import Cookies from 'js-cookie';
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { setupAuth0Session, setupSession } from '../network/axios';
import { Cookie } from '../network/enums';
import { getAuthProvider } from '../network/identityProvider/getAuthProvider';
import { Provider } from '../network/identityProvider/types';
import { stringToBoolean } from '@mvecom/common/utils/stringToBoolean';
import { isCiamEnabled, isGuestTokenEnabled } from '../utils/retailUnitsWithCiam';

export const IdentityContext = createContext(
  {} as {
    sessionState: SessionState;
    getSession: (forceUpdate?: boolean) => Promise<void>;
    getAuthProvider: () => Provider;
  },
);

export enum SessionState {
  PENDING = 'pending',
  FAILED = 'failed',
  DONE = 'done',
  IDLE = 'idle',
}

async function initializeSession() {
  if (isCiamEnabled) {
    return (await setupAuth0Session()).isAuthenticated;
  }
  return (await setupSession()).isLoggedIn;
}

export const IdentityProvider = ({ children }: PropsWithChildren) => {
  const [sessionState, setSessionState] = useState(SessionState.IDLE);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;

    setSessionState(SessionState.PENDING);
    initializeSession().then(() => setSessionState(SessionState.DONE));
  }, []);

  const get = useCallback(
    async (forceUpdate: boolean = false) => {
      if (sessionState !== SessionState.PENDING) {
        if (isCiamEnabled) {
          if (!forceUpdate) {
            const isAuthenticated = stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));

            let hasValidSession = isAuthenticated;

            if (!isAuthenticated && isGuestTokenEnabled) {
              const auth = await globalThis.ikea.mvAgent.authentication.isLoggedIn();
              hasValidSession = !!auth.guestToken;
            }

            if (hasValidSession) {
              if (sessionState !== SessionState.DONE) {
                setSessionState(SessionState.DONE);
              }
              return;
            }
          }

          setSessionState(SessionState.PENDING);
          await setupAuth0Session();
          await globalThis.ikea.mvAgent.authentication.isLoggedIn();
          setSessionState(SessionState.DONE);
        } else if (
          !forceUpdate &&
          (stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN)) ||
            (Cookies.get(Cookie.FAVOURITES_GUEST_ID) &&
              Cookies.get(Cookie.FAVOURITES_GUEST_PROVIDER) &&
              Cookies.get(Cookie.CART_GUEST_ID) &&
              Cookies.get(Cookie.CART_GUEST_PROVIDER)))
        ) {
          if (sessionState !== SessionState.DONE) {
            setSessionState(SessionState.DONE);
          }
        } else {
          setSessionState(SessionState.PENDING);
          await setupSession();
          setSessionState(SessionState.DONE);
        }
      }
    },
    [sessionState],
  );

  return (
    <IdentityContext.Provider
      value={useMemo(() => ({ sessionState, getSession: get, getAuthProvider }), [get, sessionState])}
    >
      {children}
    </IdentityContext.Provider>
  );
};
